import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuthStore, useCartStore, useChatsStore } from '@/store';
import { Icon, type IconName } from '@/components/atoms';
import { carts as cartsApi } from '@/api';
import { logger } from '@/utils/logger';
import styles from './Layout.module.css';

// F-170 B2.10 — orders merged into Chats. F-180 C13 — Cart slot
// between Чати and Шукаю майстра, surfaced only while the user has
// open carts. Nav order: Каталог / Пошук / Чати / Кошик? / Шукаю
// майстра / Я.
const baseItems: {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
  badgeKey?: 'chats';
}[] = [
  { to: '/', label: 'Каталог', icon: 'home', end: true },
  { to: '/search', label: 'Пошук', icon: 'search' },
  { to: '/chats', label: 'Чати', icon: 'chats', badgeKey: 'chats' },
  { to: '/board', label: 'Шукаю майстра', icon: 'crm' },
  { to: '/me', label: 'Я', icon: 'me' },
];

export function NavBar(): JSX.Element {
  const totalUnread = useChatsStore((s) => s.totalUnread());
  const openCartsCount = useCartStore((s) => s.openCartsCount);
  const setCount = useCartStore((s) => s.setCount);
  const user = useAuthStore((s) => s.user);

  // F-180 C13 — bootstrap the open-cart count once we have an
  // authenticated session. Logged-out visitors skip the call.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { results } = await cartsApi.fetchOpenCarts();
        if (!cancelled) setCount(results.length);
      } catch (err) {
        logger.warn('NavBar: openCarts fetch failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, setCount]);

  const items = openCartsCount > 0
    ? [
        ...baseItems.slice(0, 3),
        { to: '/cart', label: 'Кошик', icon: 'shopping-cart' as IconName },
        ...baseItems.slice(3),
      ]
    : baseItems;

  return (
    <nav className={styles.navbar}>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end ?? false}
          className={({ isActive }) =>
            [styles.navItem, isActive && styles.active].filter(Boolean).join(' ')
          }
        >
          <span className={styles.navIcon} aria-hidden>
            <Icon name={item.icon} size={22} />
          </span>
          <span>{item.label}</span>
          {/* F-170 B1.5 — single source of truth for the unread signal.
              A bare dot (no count) keeps the bottom nav glanceable and
              matches the new "no badge on the bell" rule. Accessible
              label is rendered in DOM order so SR users hear it. */}
          {item.badgeKey === 'chats' && totalUnread > 0 ? (
            <span
              className={styles.navDot}
              aria-label={`Нові повідомлення: ${totalUnread}`}
            />
          ) : null}
        </NavLink>
      ))}
    </nav>
  );
}
